import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { phCapture } from '@/lib/analytics/posthog';
import { STRINGS } from '@/strings/strings';
import { ApiError } from '@/lib/api-client';
import type { PreviewEnvelope } from '../../types';
import { regenCover, regenScene, releaseBookOrder } from '../../api';
import { Eyebrow } from '../Eyebrow';
import { StageView } from '../preview/StageView';
import { PageChangeDialog } from './PageChangeDialog';
import { StructurePageView } from './StructurePageView';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import { COVER_REGEN_TITLE_MAX_LENGTH } from '../../constants/text-limits';

/**
 * Paket 2 Jugendschutz (Sebastian ruling 2026-09-10) — FreigabeCheckHit
 * mirrors the backend `SammellaufService.FreigabeCheckHit` type. Wire-
 * shape identity: what the server puts in `envelope.debug.hits[]` on a
 * `release_content_refused` 400 is exactly what the PWA reads out here
 * and stores in local state to drive the banner + tile-highlight +
 * content-editor affordances.
 */
type FreigabeCheckHit =
  | { readonly stage: 'scene'; readonly pageNo: number }
  | { readonly stage: 'dedication' }
  | { readonly stage: 'back_cover' };

function parseFreigabeCheckHits(raw: unknown): readonly FreigabeCheckHit[] {
  if (!Array.isArray(raw)) return [];
  const out: FreigabeCheckHit[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const stage = (entry as { stage?: unknown }).stage;
    if (stage === 'scene') {
      const pageNo = (entry as { pageNo?: unknown }).pageNo;
      if (typeof pageNo === 'number' && Number.isFinite(pageNo)) {
        out.push({ stage: 'scene', pageNo });
      }
    } else if (stage === 'dedication') {
      out.push({ stage: 'dedication' });
    } else if (stage === 'back_cover') {
      out.push({ stage: 'back_cover' });
    }
  }
  return out;
}

/**
 * 8a customer review screen (Sebastian ruling 2026-08-17), extended
 * to 26 slots (Sebastian ruling 2026-09-07).
 *
 * ═══ 26-SLOT EXPANSION (2026-09-07) ═══════════════════════════════
 *
 * The printed book contains 26 elements — 1 front cover + 24 content
 * pages + 1 back cover. Prior to this expansion the review screen
 * rendered 21 slots (front cover + 20 scenes) and the customer
 * approved an EXCERPT of the book. Sebastian's ruling: the customer
 * must approve the WHOLE book. Slot order mirrors the printed book:
 *   [0] front-cover        — front cover image
 *   [1] title-page         — interior title page (title only)
 *   [2..21] scene          — scenes 1..20 in pageNo order
 *   [22] about-book        — "Über dieses Buch" (auto-composed lines)
 *   [23] about-dreamable   — "Über Dreamable" body from envelope
 *   [24] imprint           — Impressum lines from envelope
 *   [25] back-cover        — summary on navy (mirrors printed back panel)
 *
 * Change-button gate (Sebastian ruling 2026-09-07): the
 * `pageChangeCta` button appears ONLY on scene slots (2..21). All
 * six structure slots (front cover + title page + about book + about
 * Dreamable + imprint + back cover) hide the button. Cover changes
 * happen upstream on their own screens; a second entry point here
 * would let a customer change the cover after reading the whole
 * book, which is not a flow Sebastian wants.
 *
 * ═══ RELEASE BUTTON PLACEMENT (unchanged; last slot = back cover) ══
 *
 * The release button still renders at the LAST slot only (Sebastian's
 * placement ruling: "reaching the button means having been through
 * the book"). The last slot is now `back-cover` at index 25 instead
 * of scene 20 at index 20.
 *
 * ═══ WHAT THE ENVELOPE CARRIES ════════════════════════════════════
 *
 * Backend adds `aboutBook` + `structurePages` per this commit. The
 * PWA has NO local copy of the Über Dreamable / Impressum bodies by
 * design — Sebastian: "The PWA reads what the PDF composes." Every
 * string on the five typeset structure pages comes from either
 * `STRINGS.book.review` (short labels only) or the envelope.
 */

const SLIDER_TRANSITION_MS = 200;

interface FrontCoverSlot {
  readonly kind: 'front-cover';
  readonly label: string;
}

interface TitlePageSlot {
  readonly kind: 'title-page';
  readonly label: string;
}

interface SceneSlot {
  readonly kind: 'scene';
  readonly pageNo: number;
  readonly label: string;
}

interface AboutBookSlot {
  readonly kind: 'about-book';
  readonly label: string;
}

interface AboutDreamableSlot {
  readonly kind: 'about-dreamable';
  readonly label: string;
}

interface ImprintSlot {
  readonly kind: 'imprint';
  readonly label: string;
}

interface BackCoverSlot {
  readonly kind: 'back-cover';
  readonly label: string;
}

type ReviewSlot =
  | FrontCoverSlot
  | TitlePageSlot
  | SceneSlot
  | AboutBookSlot
  | AboutDreamableSlot
  | ImprintSlot
  | BackCoverSlot;

export interface ReviewScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly onRefetch: () => void;
  /**
   * Sebastian ruling 2026-09-24 — read-only reuse of this screen for
   * the post-approval book viewer (`print_approved` onward, launched
   * from "Meine Bücher"). When true, hides the five mutation controls
   * (release CTA + notice + gate hint, "Diese Seite ändern" button,
   * regen buttons, cover title-edit input, change dialogs) plus the
   * heading block and the 14-day change-scope block. Navigation
   * (chevrons + thumbnails) and page rendering stay. Consumers pass
   * their own header above; `PostOrderView` is the canonical caller.
   */
  readonly readOnly?: boolean;
}

export function ReviewScreen({ envelope, onRefetch, readOnly = false }: ReviewScreenProps) {
  const totalPages = envelope.pages.length;

  // Position-1 Commit-4 (Sebastian ruling 2026-09-04) — envelope
  // polling while any page is rendering. A customer-triggered regen
  // takes ~90 s (measured p50 87.4 s on 2026-09-04); a 6 s poll
  // interval balances responsiveness against server load. Sebastian:
  // "5-10 s while any page renders, stop when none does." Poll fires
  // ONLY when there's active work; steady state is quiet.
  //
  // Uses the shell's `onRefetch` (a stable useCallback from
  // BookRoute → BookShell). Interval cleared on unmount and on
  // "no page is rendering anymore" so the poll self-terminates as
  // soon as the customer sees the new image.
  const anySlotRendering = useMemo(
    () =>
      envelope.cover.renderState === 'rendering' ||
      envelope.pages.some((p) => p.renderState === 'rendering'),
    [envelope.cover.renderState, envelope.pages],
  );
  // Sebastian ruling 2026-09-08 (launch-slice B) — release gate.
  // Every slot (cover + 24 pages) must show `renderState === 'ready'`
  // before the customer can release. Mirror of the backend gate at
  // `src/book/release/release.service.ts` Step 3b, computed client-
  // side from the envelope so the button is disabled BEFORE the
  // customer clicks — no round-trip for a rejection the client can
  // predict. `pass_flagged` reduces to 'ready' in the derive helper
  // (both accepted and pass_flagged → 'ready'), matching the
  // backend's accepted-only SQL check (pass_flagged writers are
  // legacy grader outcomes, no new writes since QA-removal 2026-09-06).
  const allSlotsReady = useMemo(
    () =>
      envelope.cover.renderState === 'ready' &&
      envelope.pages.every((p) => p.renderState === 'ready'),
    [envelope.cover.renderState, envelope.pages],
  );

  // Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — failed-
  // slot enumeration for the top-of-screen banner + the failed-tile
  // action panel below the stage view. Banner is SUPPRESSED while any
  // slot is still rendering (motion would outdate the wording — the
  // scrim already tells the customer work is in flight). Cover +
  // scenes both contribute; the label for each slot is what the
  // customer sees on the tile ("Das Titelbild" or "Seite N").
  const failedSlotLabels = useMemo<readonly string[]>(() => {
    if (anySlotRendering) return [];
    const labels: string[] = [];
    if (envelope.cover.renderState === 'retry_available') {
      labels.push(STRINGS.book.review.failureState.slotCoverLabel);
    }
    for (const p of envelope.pages) {
      if (p.renderState === 'retry_available') {
        labels.push(STRINGS.book.review.failureState.slotSceneLabel(p.pageNo));
      }
    }
    return labels;
  }, [anySlotRendering, envelope.cover.renderState, envelope.pages]);
  const anyFailed = failedSlotLabels.length > 0;
  // Position-1 Commit-4 revisit (Sebastian ruling 2026-09-04 late-
  // evening) — arm the poll at trigger time, not at envelope-observation
  // time. The client knows it just asked for a render; it does not need
  // to discover that from the envelope. Sebastian's rule: "Do not add a
  // delayed refetch. Guessing at 1500 ms works on your machine and
  // fails on a slow one." Set true in `handleRegenSaved`; cleared once
  // the envelope catches up and `anyPageRendering` flips true (the
  // envelope-based signal takes over from the client-armed one).
  const [pollActive, setPollActive] = useState(false);
  useEffect(() => {
    if (!anySlotRendering && !pollActive) return;
    const id = window.setInterval(() => onRefetch(), 6000);
    return () => window.clearInterval(id);
  }, [anySlotRendering, pollActive, onRefetch]);
  useEffect(() => {
    if (pollActive && anySlotRendering) setPollActive(false);
  }, [pollActive, anySlotRendering]);

  // Build the 26-slot list once per envelope change. Order mirrors
  // the printed book (front cover → interior title page → 20 scenes
  // → about-book → about-Dreamable → imprint → back cover). Scene
  // slots come in `pageNo` order (envelope already sorts them
  // ascending per PreviewReadService's Prisma orderBy); `pageNo`
  // semantics stay the same — only the slot INDEX shifts (scene 1 is
  // now at index 2 instead of index 1).
  const slots = useMemo<readonly ReviewSlot[]>(() => {
    const frontCover: FrontCoverSlot = {
      kind: 'front-cover',
      label: STRINGS.book.review.frontCoverLabel,
    };
    const titlePage: TitlePageSlot = {
      kind: 'title-page',
      label: STRINGS.book.review.titlePageLabel,
    };
    const scenes: SceneSlot[] = envelope.pages.map((p) => ({
      kind: 'scene',
      pageNo: p.pageNo,
      label: STRINGS.book.review.pageLabel(p.pageNo, totalPages),
    }));
    const aboutBook: AboutBookSlot = {
      kind: 'about-book',
      label: STRINGS.book.review.aboutBookLabel,
    };
    const aboutDreamable: AboutDreamableSlot = {
      kind: 'about-dreamable',
      label: STRINGS.book.review.aboutDreamableLabel,
    };
    const imprint: ImprintSlot = {
      kind: 'imprint',
      label: STRINGS.book.review.imprintLabel,
    };
    const backCover: BackCoverSlot = {
      kind: 'back-cover',
      label: STRINGS.book.review.backCoverLabel,
    };
    return [frontCover, titlePage, ...scenes, aboutBook, aboutDreamable, imprint, backCover];
  }, [envelope.pages, totalPages]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [releaseState, setReleaseState] = useState<
    | { kind: 'idle' }
    | { kind: 'pending' }
    | { kind: 'error'; message: string }
    | { kind: 'content_refused'; hits: readonly FreigabeCheckHit[] }
    | { kind: 'content_refused_exhausted' }
    | { kind: 'done' }
  >({ kind: 'idle' });

  // Paket 2 Jugendschutz (Sebastian ruling 2026-09-10) — content-refused
  // slot labels. Sourced from the local state written by the release
  // handler's `release_content_refused` branch. The banner and tile
  // highlights read from these — one banner slot type per hit.
  const contentRefusedHits: readonly FreigabeCheckHit[] =
    releaseState.kind === 'content_refused' ? releaseState.hits : [];
  const contentRefusedLabels = useMemo<readonly string[]>(() => {
    const labels: string[] = [];
    for (const hit of contentRefusedHits) {
      if (hit.stage === 'scene') {
        labels.push(STRINGS.book.review.failureState.slotSceneLabel(hit.pageNo));
      } else if (hit.stage === 'dedication') {
        labels.push(STRINGS.book.review.failureState.slotDedicationLabel);
      } else {
        labels.push(STRINGS.book.review.failureState.slotBackCoverLabel);
      }
    }
    return labels;
  }, [contentRefusedHits]);
  const anyContentRefused = contentRefusedLabels.length > 0;

  const bannerText = useMemo<string | null>(() => {
    // Content-refused banner takes precedence over image-failure banner
    // (customer must edit the refused text before he can re-release).
    if (anyContentRefused) {
      if (contentRefusedLabels.length === 1) {
        return STRINGS.book.review.failureState.bannerContentSingle.replace(
          '{SLOT}',
          contentRefusedLabels[0]!,
        );
      }
      return STRINGS.book.review.failureState.bannerContentMultiple.replace(
        '{SLOTS}',
        contentRefusedLabels.join(', '),
      );
    }
    if (!anyFailed) return null;
    if (failedSlotLabels.length === 1) {
      return STRINGS.book.review.failureState.bannerSingle.replace('{SLOT}', failedSlotLabels[0]!);
    }
    return STRINGS.book.review.failureState.bannerMultiple.replace(
      '{SLOTS}',
      failedSlotLabels.join(', '),
    );
  }, [anyFailed, failedSlotLabels, anyContentRefused, contentRefusedLabels]);

  // Position-1 Commit-2 (Sebastian rulings 2026-09-03 + 2026-09-04) —
  // per-slot Change dialog. Boolean state: dialog opens for the
  // currently-active slot; closing wipes state.
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const openChangeDialog = useCallback(() => setChangeDialogOpen(true), []);
  const closeChangeDialog = useCallback(() => setChangeDialogOpen(false), []);

  // Position-1 Commit-4 revisit — wrap the child's `onSaved` so a
  // successful regen (a) refetches immediately and (b) arms the poll
  // before the envelope has had a chance to reflect the new pending
  // row. See `pollActive` above.
  const handleRegenSaved = useCallback(() => {
    onRefetch();
    setPollActive(true);
  }, [onRefetch]);

  // Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — inline
  // failed-tile retry state. Fires when the customer clicks "Nochmal
  // zeichnen" on a scene tile OR "Nochmal zeichnen" on the cover tile
  // OR saves the inline title-edit path on the cover tile. Uses the
  // same handleRegenSaved as the change-dialog paths so the poll arms
  // + the envelope refetches identically.
  const [failedRetryPending, setFailedRetryPending] = useState(false);
  const [failedRetryError, setFailedRetryError] = useState<string | null>(null);
  const handleFailedRetryScene = useCallback(
    async (sceneId: string) => {
      setFailedRetryPending(true);
      setFailedRetryError(null);
      try {
        await regenScene(envelope.bookOrderId, sceneId, null);
        handleRegenSaved();
      } catch (err) {
        // Server-side gates that can still fire here:
        //   scene_retry_exhausted (409) — customer already at cap
        //   scene_regen_page_cap_reached (409) — pre-existing cap
        //   scene_regen_not_in_customer_review (409) — stale state
        // All three surface as generic try-again copy — the released
        // failure-path copy does not distinguish. Envelope refetch on
        // the next poll cycle updates retryExhausted / renderState so
        // the button either disables or re-arms.
        void err;
        setFailedRetryError(STRINGS.book.review.releaseErrorGeneric);
      } finally {
        setFailedRetryPending(false);
      }
    },
    [envelope.bookOrderId, handleRegenSaved],
  );
  const handleFailedRetryCover = useCallback(async () => {
    setFailedRetryPending(true);
    setFailedRetryError(null);
    try {
      // Bare retry: null on both fields. The `CoverRegenService` both-
      // empty guard is RELAXED when the latest cover AnchorAttempt is
      // in `error` status (Sebastian ruling 2026-09-10). On the failed-
      // tile path the latest attempt IS in error by construction
      // (renderState === 'retry_available'), so the server accepts the
      // bare retry.
      await regenCover(envelope.bookOrderId, { title: null, comment: null });
      handleRegenSaved();
    } catch (err) {
      void err;
      setFailedRetryError(STRINGS.book.review.releaseErrorGeneric);
    } finally {
      setFailedRetryPending(false);
    }
  }, [envelope.bookOrderId, handleRegenSaved]);
  const handleCoverAnotherTitleSave = useCallback(
    async (newTitle: string) => {
      setFailedRetryPending(true);
      setFailedRetryError(null);
      try {
        await regenCover(envelope.bookOrderId, { title: newTitle, comment: null });
        handleRegenSaved();
      } catch (err) {
        void err;
        setFailedRetryError(STRINGS.book.review.releaseErrorGeneric);
      } finally {
        setFailedRetryPending(false);
      }
    },
    [envelope.bookOrderId, handleRegenSaved],
  );

  const handlePrev = useCallback(() => {
    setActiveIndex((i) => Math.max(0, i - 1));
  }, []);
  const handleNext = useCallback(() => {
    setActiveIndex((i) => Math.min(slots.length - 1, i + 1));
  }, [slots.length]);

  const handleThumbClick = useCallback((i: number) => {
    setActiveIndex(i);
  }, []);

  const handleRelease = useCallback(async () => {
    setReleaseState({ kind: 'pending' });
    try {
      await releaseBookOrder(envelope.bookOrderId);
      // Sebastian ruling 2026-10-08 — book_print_approved fires after
      // the server-confirmed release. Order-scoped dedup via
      // localStorage so a repeat in a later session does not re-fire
      // (the backend's own idempotence already covers the backend
      // side; this guards the funnel counter).
      try {
        const key = `book_print_approved_${envelope.bookOrderId}`;
        if (window.localStorage.getItem(key) !== '1') {
          if (phCapture('book_print_approved', { book_order_id: envelope.bookOrderId })) {
            window.localStorage.setItem(key, '1');
          }
        }
      } catch {
        // storage / capture errors are non-fatal
      }
      setReleaseState({ kind: 'done' });
      // Refetch so BookRoute re-dispatches on the new print_approved
      // status. The downstream screen is out of 8a scope.
      onRefetch();
    } catch (err) {
      const code = err instanceof ApiError ? err.errorCode : null;
      // Paket 2 Jugendschutz (Sebastian ruling 2026-09-10) — the
      // release endpoint fires the Sammellauf content check between
      // the readiness gate and the state-machine flip. Two refusal
      // paths reach the client:
      //   - release_content_refused           → hits carried in
      //                                          envelope.debug.hits;
      //                                          store in local state,
      //                                          banner + tile
      //                                          highlights render off
      //                                          the state.
      //   - release_content_refused_exhausted → cap-exhausted (10
      //                                          refusals on same book);
      //                                          render the released
      //                                          support-line copy in
      //                                          place of the retry
      //                                          button.
      if (code === 'release_content_refused' && err instanceof ApiError) {
        const rawHits = err.envelope?.debug.hits;
        const hits = parseFreigabeCheckHits(rawHits);
        setReleaseState({ kind: 'content_refused', hits });
        return;
      }
      if (code === 'release_content_refused_exhausted') {
        setReleaseState({ kind: 'content_refused_exhausted' });
        return;
      }
      const message =
        code === 'book_release_not_ready'
          ? STRINGS.book.review.releaseErrorNotReady
          : STRINGS.book.review.releaseErrorGeneric;
      setReleaseState({ kind: 'error', message });
    }
  }, [envelope.bookOrderId, onRefetch]);

  // `slots` is built above as a 26-entry array (front cover + title
  // page + N scenes + about-book + about-Dreamable + imprint + back
  // cover) and is therefore always non-empty. `activeIndex` is a
  // state variable clamped between 0 and slots.length-1 by
  // handlePrev/handleNext. `slots[activeIndex]` cannot be undefined
  // at runtime — but `noUncheckedIndexedAccess` requires the guard.
  // `return null` is the safe no-op if the invariant is ever violated
  // (e.g. by a race on refetch that returns an empty envelope),
  // rather than silently rendering a broken screen.
  const activeSlot = slots[activeIndex];
  if (!activeSlot) return null;

  // Two-mode active-slot rendering (Sebastian ruling 2026-09-07):
  //   - image slots (front-cover, scene) → StageView
  //   - typeset slots (title-page, about-book, about-dreamable,
  //     imprint, back-cover) → StructurePageView
  // The dispatch is on the discriminator, not on a shared shape, so
  // a new slot kind added later cannot silently fall through the
  // wrong branch.
  const stageSlot = buildStageSlotForImage(activeSlot, envelope);

  // Change-button gate (Sebastian ruling 2026-09-08 supersedes the
  // 2026-09-07 evening ruling on the front-cover): scene slots AND
  // front-cover. The 2026-09-07 rule hid the button on the front
  // cover "because cover changes happen upstream" — that was a step
  // backwards; the combined title-and-image regen dialog for the
  // front cover was already built + working and removing the entry
  // point here served no one. Restored to the pre-3891384 shape.
  // Back-cover slot gains its own button in a follow-up commit
  // (Point 2, back-cover summary edit) so the button + its dialog
  // wiring land together.
  // Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — is the
  // ACTIVE slot the one showing a failed render? When true, the
  // failed-tile action panel replaces the standard change button:
  // customer sees "Nochmal zeichnen" (+ "Anderer Titel" on the cover)
  // OR the cap-exhausted line when retryExhausted is true. The
  // failed-tile UI hides the "Diese Seite ändern" affordance entirely
  // (the failed state is a different mode).
  const activeSlotFailed =
    (activeSlot.kind === 'front-cover' && envelope.cover.renderState === 'retry_available') ||
    (activeSlot.kind === 'scene' &&
      envelope.pages.find((p) => p.pageNo === activeSlot.pageNo)?.renderState ===
        'retry_available');
  const activeSceneRetryExhausted =
    activeSlot.kind === 'scene' &&
    (envelope.pages.find((p) => p.pageNo === activeSlot.pageNo)?.retryExhausted ?? false);
  const activeCoverRetryExhausted =
    activeSlot.kind === 'front-cover' && envelope.cover.regen.retryExhausted;
  const activeSlotRetryExhausted = activeSceneRetryExhausted || activeCoverRetryExhausted;

  const showChangeButton =
    !readOnly &&
    (activeSlot.kind === 'scene' || activeSlot.kind === 'front-cover') &&
    !activeSlotFailed;

  return (
    <div className="mx-auto flex w-full max-w-[680px] flex-col gap-4 px-4 pt-2 pb-6">
      {/* Block 7 · 7.1 · Eyebrow + h1 + book title. Book title
          renders in bold below the heading when non-empty
          (envelope.title is always populated by the time this
          screen fires — post-payment). Hidden in readOnly mode —
          PostOrderView owns the wrapper header there. */}
      {!readOnly && (
        <div className="flex flex-col gap-3">
          <Eyebrow text={STRINGS.book.review.eyebrow} />
          <h1 className="text-2xl font-semibold text-dream-navy">{STRINGS.book.review.heading}</h1>
          {envelope.title.length > 0 && (
            <p className="text-lg font-semibold text-dream-navy">„{envelope.title}"</p>
          )}
        </div>
      )}

      {/* Clause 3.3 change-scope block (Sebastian ruling 2026-09-13).
          Names the scope of permitted changes + the 14-day deadline
          at preview-provision time, per the Nutzungsbedingungen §3.3
          requirement. Renders below the heading, above the failure-
          banner. The urgency countdown line is wired but stays dark
          until slice 2 populates envelope.daysUntilAutoprint. Hidden
          in readOnly mode — the deadline is post-approval irrelevant
          and the change scope is exactly what the customer no longer
          has. */}
      {!readOnly && <ChangeScopeBlock daysUntilAutoprint={envelope.daysUntilAutoprint} />}

      {/* Jugendschutz launch-slice (Sebastian ruling 2026-09-10) —
          failure-path banner. Renders at the top of the screen when
          at least one slot has renderState === 'retry_available'
          AND no slot is still rendering. Text names the failed slot
          (or slots, comma-joined) using the released copy verbatim.
          Suppressed while any slot renders — motion would outdate
          the wording. */}
      {!readOnly && bannerText !== null && (
        <div
          role="alert"
          className="rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-dream-navy"
        >
          {bannerText}
        </div>
      )}

      {/* Big stage — image slots go through StageView, typeset
          structure slots go through StructurePageView. Chevron nav +
          square container are shared between the two primitives; the
          differ is what they draw inside. Block 7 · 7.2 · slot 0
          is `front-cover` so the initial view is the cover image
          large above the tile grid, first thing the customer sees. */}
      {stageSlot !== null ? (
        <StageView
          slot={stageSlot}
          canGoPrev={activeIndex > 0}
          canGoNext={activeIndex < slots.length - 1}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      ) : (
        <StructurePageView
          slot={buildStructureSlot(activeSlot, envelope)}
          ariaLabel={activeSlot.label}
          canGoPrev={activeIndex > 0}
          canGoNext={activeIndex < slots.length - 1}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}

      {/* Active-slot label */}
      <p className="text-center text-sm font-medium text-dream-navy">{activeSlot.label}</p>

      {/* Jugendschutz launch-slice (Sebastian ruling 2026-09-10) —
          failed-tile action panel. Renders below the stage view when
          the ACTIVE slot has renderState === 'retry_available'.
          Replaces the standard "Diese Seite ändern" button entirely
          (the failed state is a different mode; the customer needs a
          retry, not a menu of edit options).

          Sebastian correction slice 2026-09-10:
          - Part 3.1 (scene fail — technical OR content): renders
            `Konnte nicht gezeichnet werden` line + `Nochmal zeichnen`
            button. Unchanged from 025749c.
          - Part 3.2 (cover fail — technical): renders `Nochmal
            zeichnen` ONLY. The pre-slice "Anderer Titel" second
            button is DROPPED for technical cover failures.
          - Part 3.3 (cover REFUSED on content): renders inline title-
            input prefilled with `envelope.title` + "Titel ändern und
            neu zeichnen" rebuild button on the sharpened tile line
            "Konnte nicht gezeichnet werden. Das kann an eurem Titel
            liegen." Discriminator: `envelope.cover.contentRefused`.

          When retryExhausted is true the buttons are replaced by the
          released cap-exhausted line — server refuses further retries
          with `scene_retry_exhausted` / `cover_retry_exhausted`. */}
      {!readOnly && activeSlotFailed && (
        <div className="flex flex-col gap-2">
          {/* Sharpened tile line for cover-content-refused (Part 3.3),
              standard tile line for every other failure. */}
          <p className="text-center text-sm text-dream-navy/80">
            {activeSlot.kind === 'front-cover' && (envelope.cover.contentRefused ?? false)
              ? STRINGS.book.review.failureState.coverContentRefused.tileLine
              : STRINGS.book.review.failureState.tileFailedLine}
          </p>
          {activeSlotRetryExhausted ? (
            <p className="text-center text-sm text-dream-navy">
              {STRINGS.book.review.failureState.capExhaustedLine}
            </p>
          ) : activeSlot.kind === 'front-cover' && (envelope.cover.contentRefused ?? false) ? (
            /* Part 3.3 — cover content-refused. Inline title input +
               rebuild button. No "Nochmal zeichnen" / "Anderer Titel"
               pair; the customer edits the title and submits. */
            <CoverContentRefusedInput
              initialTitle={envelope.title ?? ''}
              onSave={handleCoverAnotherTitleSave}
              pending={failedRetryPending}
            />
          ) : (
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (activeSlot.kind === 'front-cover') {
                    void handleFailedRetryCover();
                  } else if (activeSlot.kind === 'scene') {
                    const page = envelope.pages.find((p) => p.pageNo === activeSlot.pageNo);
                    if (page) void handleFailedRetryScene(page.sceneId);
                  }
                }}
                disabled={failedRetryPending}
                aria-busy={failedRetryPending}
                className="flex h-10 items-center justify-center gap-2 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-60"
              >
                {failedRetryPending && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
                {STRINGS.book.review.failureState.retryButton}
              </button>
              {/* Part 3.2 (Sebastian correction slice 2026-09-10) —
                  the pre-slice "Anderer Titel" second button on the
                  cover-technical branch is DROPPED. Only "Nochmal
                  zeichnen" remains for technical cover failures; the
                  content-refused branch above owns its own title-edit
                  affordance. */}
            </div>
          )}
          {failedRetryError !== null && (
            <p role="alert" className="text-center text-sm text-error">
              {failedRetryError}
            </p>
          )}
        </div>
      )}

      {/* Change button — scene slots only. Front cover + all five
          typeset structure slots hide the button per Sebastian
          ruling 2026-09-07. */}
      {showChangeButton && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={openChangeDialog}
            className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5"
          >
            {STRINGS.book.review.pageChangeCta}
          </button>
        </div>
      )}

      {!readOnly && changeDialogOpen && activeSlot.kind === 'scene' && (
        <PageChangeDialogForPage
          envelope={envelope}
          pageNo={activeSlot.pageNo}
          onClose={closeChangeDialog}
          onSaved={onRefetch}
          onRegenSaved={handleRegenSaved}
        />
      )}

      {/* Sebastian ruling 2026-09-08 — cover-variant PageChangeDialog
          restored (was removed at 3891384; put back verbatim to the
          pre-3891384 shape). The combined title-and-image regen path
          the dialog opens is unchanged. */}
      {!readOnly && changeDialogOpen && activeSlot.kind === 'front-cover' && (
        <PageChangeDialog
          kind="cover"
          bookOrderId={envelope.bookOrderId}
          initialTitle={envelope.title ?? ''}
          onClose={closeChangeDialog}
          onSaved={handleRegenSaved}
          renderState={envelope.cover.renderState}
          regen={envelope.cover.regen}
        />
      )}

      {/* Thumbnail strip — horizontally scrollable, snap-to-thumbnail.
          Auto-scrolling the active thumbnail into view uses the
          browser's built-in behavior via `scrollIntoView` in a
          layout effect wired below. */}
      <ThumbStrip
        slots={slots}
        envelope={envelope}
        activeIndex={activeIndex}
        onThumbClick={handleThumbClick}
      />

      {/* Release button — permanently visible across all 26 slots
          (Sebastian ruling 2026-09-08 supersedes the 2026-09-07 last-
          slot placement rule). Reason: "A customer who does not want
          to page through the whole book decides that himself."
          Ruling change is customer-agency, not visual layout.
          Terminal states (pending / error / done) render inline in
          place of the fresh CTA the same way they did before. */}
      {!readOnly && releaseState.kind === 'idle' && (
        // Sebastian ruling 2026-09-08 (launch-slice B) — release
        // gate. When any slot is not `ready`, the button is disabled
        // and a short hint names why. Backend gate at
        // `src/book/release/release.service.ts` Step 3b enforces the
        // same condition server-side; this client-side gate exists
        // so the customer sees the affordance state honestly (no
        // click → server-round-trip → error path when the client can
        // predict the reject).
        <>
          {!allSlotsReady && (
            <p className="mt-2 text-center text-sm text-dream-navy/70">
              {STRINGS.book.review.releaseGateHint}
            </p>
          )}
          {/* Block 7 · 7.3 · Release notice — sits directly above the
              release button. Verbatim from Sebastian's spec: names
              the irreversibility of the release + the print-what-you-
              see guarantee. Renders in every idle state (before AND
              after the gate hint above, which only appears when slots
              are not ready). */}
          <p className="mt-2 text-center text-sm text-dream-navy">
            {STRINGS.book.review.releaseNotice}
          </p>
          <button
            type="button"
            onClick={handleRelease}
            disabled={!allSlotsReady}
            className="mt-2 h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-dream-navy/60 disabled:opacity-60 disabled:hover:opacity-60"
          >
            {STRINGS.book.review.releaseCta}
          </button>
        </>
      )}
      {!readOnly && releaseState.kind === 'pending' && (
        <button
          type="button"
          disabled
          aria-busy="true"
          className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-dream-gold/60 px-8 text-base font-bold text-dream-navy"
        >
          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          {STRINGS.book.review.releaseBusy}
        </button>
      )}
      {!readOnly && releaseState.kind === 'error' && (
        <div className="mt-2 flex flex-col gap-2">
          <p role="alert" className="text-sm text-error">
            {releaseState.message}
          </p>
          <button
            type="button"
            onClick={handleRelease}
            className="h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:opacity-90"
          >
            {STRINGS.book.review.releaseCta}
          </button>
        </div>
      )}
      {/* Paket 2 Jugendschutz (Sebastian ruling 2026-09-10) — content-
          refused state. The top-of-screen banner already names the
          affected slots (bannerContentSingle / bannerContentMultiple);
          this block below the thumb strip re-offers a retry so the
          customer can release again after editing. Hits stay in local
          state until the release call succeeds or the customer
          navigates away — the tile highlights and banner remain
          visible during the edit flow. */}
      {!readOnly && releaseState.kind === 'content_refused' && (
        <div className="mt-2 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleRelease}
            className="h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:opacity-90"
          >
            {STRINGS.book.review.releaseCta}
          </button>
        </div>
      )}
      {/* Paket 2 Jugendschutz (Sebastian ruling 2026-09-10) — cap
          exhausted (10 refusals on the same book). Renders the
          released support-line copy in place of the retry button.
          Hides the release CTA entirely — a further click cannot
          change the outcome; the customer's affordance is to write
          support@dreamable.kids. */}
      {!readOnly && releaseState.kind === 'content_refused_exhausted' && (
        <div className="mt-2 flex flex-col items-center gap-2">
          <p className="text-center text-sm text-dream-navy">
            {STRINGS.book.review.failureState.capExhaustedContentLine}
          </p>
        </div>
      )}
      {!readOnly && releaseState.kind === 'done' && (
        <div className="mt-2 flex flex-col items-center gap-2">
          <h2 className="text-base font-semibold text-dream-navy">
            {STRINGS.book.review.releasedHeading}
          </h2>
          <p className="text-center text-sm text-dream-navy/80">
            {STRINGS.book.review.releasedBody}
          </p>
        </div>
      )}

      {/* Transition ms unused visually — kept as a named constant so
          the future add-a-slide-animation change has one knob. */}
      <span className="sr-only">{SLIDER_TRANSITION_MS}</span>
    </div>
  );
}

/**
 * Clause 3.3 change-scope block (Sebastian ruling 2026-09-13).
 *
 * Renders the scope of permitted changes + the 14-day deadline
 * sentence at preview-provision time, satisfying the
 * Nutzungsbedingungen §3.3 requirement that the customer be told
 * what he may change and by when. Placed below the h1 heading on
 * the release screen, above the failure banner.
 *
 * Urgency line (rendered ABOVE the block, wrapped in this component
 * for co-location) shows when `daysUntilAutoprint ∈ {1, 2, 3}`.
 * Hidden when the value is undefined (slice 1 pre-slice-2 state),
 * null (Option A backfill — clock never started), or > 3. Slice 2
 * populates the envelope value; until then this line stays dark
 * by design (Sebastian ruling 2026-09-13: "the countdown is
 * comfort").
 */
function ChangeScopeBlock({
  daysUntilAutoprint,
}: {
  readonly daysUntilAutoprint: number | null | undefined;
}) {
  const showUrgency =
    typeof daysUntilAutoprint === 'number' && daysUntilAutoprint >= 1 && daysUntilAutoprint <= 3;
  return (
    <section
      aria-label={STRINGS.book.review.changeScopeIntro}
      className="flex flex-col gap-3 text-base leading-relaxed text-dream-navy"
    >
      {showUrgency && (
        <p className="font-semibold">{STRINGS.book.review.urgencyLine(daysUntilAutoprint)}</p>
      )}
      <p>{STRINGS.book.review.changeScopeIntro}</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>{STRINGS.book.review.changeScopeItemText}</li>
        <li>{STRINGS.book.review.changeScopeItemPanel}</li>
        <li>{STRINGS.book.review.changeScopeItemImage}</li>
      </ul>
      <p>{STRINGS.book.review.changeScopeDeadline}</p>
    </section>
  );
}

/**
 * Build the `StageView` slot shape for a given ReviewSlot + envelope,
 * OR return `null` when the slot is a typeset structure slot that
 * belongs to `StructurePageView` instead (see `buildStructureSlot`).
 *
 * Two-mode dispatch (Sebastian ruling 2026-09-07): image slots
 * (`front-cover`, `scene`) route to `StageView`; typeset slots
 * (`title-page`, `about-book`, `about-dreamable`, `imprint`,
 * `back-cover`) route to `StructurePageView`. The caller reads the
 * `null` return as "this is a typeset slot, ask `buildStructureSlot`
 * for the shape."
 */
function buildStageSlotForImage(
  slot: ReviewSlot,
  envelope: PreviewEnvelope,
): Parameters<typeof StageView>[0]['slot'] | null {
  if (slot.kind === 'front-cover') {
    return {
      kind: 'cover',
      imageUrl: envelope.cover.imageUrl,
      imageAlt: STRINGS.book.review.frontCoverLabel,
      // Cover-regen slice — thread renderState + runningLabel through
      // so StageView paints the scrim over the cover image while a
      // cover regen is in flight, symmetric with the page path.
      renderState: envelope.cover.renderState,
      runningLabel: STRINGS.book.review.regenRunningLabel,
      // Sebastian ruling 2026-09-08 — post-payment failed cover
      // gets the review-flavour tile-failed copy (support address).
      retryLabel: STRINGS.book.review.tileFailed,
    };
  }
  if (slot.kind !== 'scene') {
    // Typeset structure slot — StructurePageView handles it.
    return null;
  }
  const page = envelope.pages.find((p) => p.pageNo === slot.pageNo);
  if (!page) {
    // Defensive fallback — should not happen because slots is built
    // from envelope.pages above. Rendering the placeholder is safer
    // than the alternative (a stale slot with a stale renderState from
    // a refetch race).
    return {
      kind: 'page',
      composedImageUrl: null,
      rawImageUrl: null,
      imageAlt: slot.label,
      runningLabel: STRINGS.book.review.regenRunningLabel,
    };
  }
  return {
    kind: 'page',
    composedImageUrl: page.composedImageUrl,
    // Position-1 Commit-3 revert (Sebastian ruling 2026-09-03 evening,
    // after the live test on 1b17043d showed the stage without a
    // panel while the thumbnails still had one). The tsc error on
    // rawImageUrl was a SIGNAL, not noise — the stage was never
    // meant to consume the raw image. On the Freigabe screen the
    // customer must see the FINISHED page, panel + text included;
    // the raw image belongs to the drag surface only. StageView's
    // fallback `raw ?? composed` reads null here and lands on the
    // composed URL — which is what the customer expects. Closing
    // the type error in the other direction hid the rule. Second
    // time in one day a red thing turned out to be load-bearing
    // (first: the "arbitrary German at cap fits" invariant, deleted
    // then restored in the corpus-probe slice).
    rawImageUrl: null,
    imageAlt: slot.label,
    // Position-1 Commit-4 revisit — thread renderState and the running
    // label through so StageView paints the scrim over the big image
    // while a regen is in flight. Read on every render; navigating
    // away with the arrows and back re-derives the same envelope-driven
    // decision. `page` is narrowed non-undefined by the guard above,
    // so `page.renderState` is `CustomerRenderState` (required on
    // PreviewPage per types.ts:319).
    renderState: page.renderState,
    runningLabel: STRINGS.book.review.regenRunningLabel,
    // Sebastian ruling 2026-09-08 — post-payment scene tile-failed
    // wording (support address). Fires when the scene's latest
    // RenderAttempt resolved to `retry_available` (failed_final or
    // error terminal) — the release gate below prevents this from
    // reaching print, and this copy tells the customer we're on it.
    retryLabel: STRINGS.book.review.tileFailed,
  };
}

/**
 * Build the `StructurePageView` slot shape for a typeset slot.
 *
 * Caller invariant (Sebastian ruling 2026-09-07): only fires when
 * `buildStageSlotForImage` returns `null`, i.e. the active slot is
 * one of the five typeset kinds. `switch` is exhaustive over the
 * typeset union; the `throw` in the impossible-default branch
 * documents the invariant AND makes the TypeScript inference exact
 * (no widening to `undefined`).
 */
function buildStructureSlot(
  slot: ReviewSlot,
  envelope: PreviewEnvelope,
): Parameters<typeof StructurePageView>[0]['slot'] {
  switch (slot.kind) {
    case 'title-page':
      // Sebastian ruling 2026-09-16 — title page mirrors the printer
      // (title + anchor image + Die Crew + cast + date). Every field
      // sourced from the envelope so the client stays a pure reader.
      return {
        kind: 'title-page',
        title: envelope.title,
        anchorImageUrl: envelope.cover.imageUrl,
        castMembers: envelope.cover.castMembers,
        creationDateGerman: envelope.aboutBook.creationDateGerman,
      };
    case 'about-book':
      // Sebastian ruling 2026-09-14 (revised) — page 22 is now the
      // dedication blank ("Diese Seite gehört euch." headline only).
      // The `about-book` slot kind name predates the revision and
      // stays for internal-code stability; the renderer inside
      // StructurePageView is now `DedicationBlankBody`. No envelope
      // fields flow through the slot shape anymore.
      return { kind: 'about-book' };
    case 'about-dreamable':
      return {
        kind: 'about-dreamable',
        paragraphs: envelope.structurePages.ueberDreamable,
      };
    case 'imprint':
      return { kind: 'imprint', lines: envelope.structurePages.impressum };
    case 'back-cover':
      // Sebastian ruling 2026-09-16 — back cover mirrors the printed
      // back panel (Dreamable logo + marking block at the foot).
      // backMarking sourced from the envelope so a printer-side
      // constant edit flows through automatically.
      return {
        kind: 'back-cover',
        backMarking: envelope.structurePages.backMarking,
      };
    case 'front-cover':
    case 'scene':
      // Impossible under caller invariant — front-cover + scene are
      // handled by StageView (buildStageSlotForImage returns a shape
      // for them, not null). The throw is a fail-loud regression
      // guard: if a future refactor routes an image slot here by
      // mistake, the customer sees a hard error instead of a silently
      // wrong page.
      throw new Error(`buildStructureSlot invoked on image slot kind=${slot.kind}`);
  }
}

/**
 * Wrapper that resolves the active page slot's envelope fields for
 * the page-variant of PageChangeDialog. Kept as a thin component so
 * the ReviewScreen JSX stays readable AND so the page-not-found
 * fallback (should never fire) has a clear no-op branch.
 */
function PageChangeDialogForPage({
  envelope,
  pageNo,
  onClose,
  onSaved,
  onRegenSaved,
}: {
  readonly envelope: PreviewEnvelope;
  readonly pageNo: number;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly onRegenSaved: () => void;
}) {
  const page = envelope.pages.find((p) => p.pageNo === pageNo);
  if (!page) {
    // Envelope invariant: every pageNo in the slot list came from
    // envelope.pages. This branch should never fire; noop-close if
    // it does.
    return null;
  }
  return (
    <PageChangeDialog
      kind="page"
      bookOrderId={envelope.bookOrderId}
      sceneId={page.sceneId}
      initialText={page.text}
      maxCharsForPanel={page.maxCharsForPanel}
      drawnPanel={page.drawnPanel}
      rawImageUrl={page.rawImageUrl}
      {...(envelope.panelGeometry !== undefined ? { panelGeometry: envelope.panelGeometry } : {})}
      // Position-1 Commit-4 — the dialog needs renderState (to
      // disable option 3 while a regen is already running on this
      // page) and remainingPage (to render the ability-framed line
      // under option 3 after first use). Both are envelope surfaces
      // that already existed; the dialog just reads them now.
      renderState={page.renderState}
      regenRemainingPage={page.remainingPage}
      // Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) —
      // menu-entry visibility gate signal. `page.usedPage > 0` iff
      // more than one image exists for this scene (post QA-removal
      // every customer_regen reaches accepted).
      usedPage={page.usedPage}
      onClose={onClose}
      onSaved={onSaved}
      onRegenSaved={onRegenSaved}
    />
  );
}

interface ThumbStripProps {
  readonly slots: readonly ReviewSlot[];
  readonly envelope: PreviewEnvelope;
  readonly activeIndex: number;
  readonly onThumbClick: (i: number) => void;
}

/**
 * Horizontally scrollable thumbnail strip. 26 tiles at ~56 px each
 * exceed the 375 px mobile viewport; the strip scrolls
 * (`overflow-x-auto` + snap). Each thumbnail is a square with either
 * an image (front cover + scene slots — signed URL from the
 * envelope) or a miniature-text rendering (five typeset structure
 * slots).
 *
 * Miniature-text thumbnails (Sebastian ruling 2026-09-07: "small
 * version of the page"): each of the five typeset slots renders a
 * ~10px label centered in the tile, mirroring the slot content in a
 * one-line form. Back cover uses navy background + white text
 * (mirroring the printed back panel). No shared icon library — the
 * PWA is not adding icon vocabulary for these five surfaces.
 */
function ThumbStrip({ slots, envelope, activeIndex, onThumbClick }: ThumbStripProps) {
  return (
    <div
      role="tablist"
      aria-label={STRINGS.book.review.slotAriaLabel}
      className="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1"
    >
      {slots.map((slot, i) => {
        // Only scene slots have a `page` to consult for renderState.
        const page =
          slot.kind === 'scene' ? envelope.pages.find((p) => p.pageNo === slot.pageNo) : undefined;
        // Image slots consult the envelope's signed URLs. Typeset
        // slots have no image; the miniature-text renderer below
        // handles them.
        const imageUrl =
          slot.kind === 'front-cover'
            ? envelope.cover.imageUrl
            : slot.kind === 'scene'
              ? (page?.composedImageUrl ?? null)
              : null;
        const isActive = i === activeIndex;
        // Rendering pulse mirrors the stage scrim so the customer
        // sees where the work is even when scrolled elsewhere in the
        // thumbnail lane. Only meaningful on scene slots.
        const isRendering = slot.kind === 'scene' && page?.renderState === 'rendering';
        const isBackCoverTypeset = slot.kind === 'back-cover';
        return (
          <button
            key={thumbKey(slot)}
            role="tab"
            aria-selected={isActive}
            aria-label={slot.label}
            onClick={() => onThumbClick(i)}
            className={
              (isActive
                ? 'ring-2 ring-dream-navy '
                : 'ring-1 ring-dream-navy/20 hover:ring-dream-navy/40 ') +
              (isRendering ? 'animate-pulse ' : '') +
              (isBackCoverTypeset ? 'bg-dream-navy ' : 'bg-dream-white ') +
              'relative h-14 w-14 flex-shrink-0 snap-start overflow-hidden rounded-md'
            }
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                // Sebastian ruling 2026-09-24 — 26-tile strip shares
                // the composed URL (~150-300 KB @ 900×900) with the
                // stage view. Without lazy the browser opens 26
                // concurrent fetches on mount and blocks the stage
                // image behind them. Lazy defers off-screen tiles
                // until they enter viewport; on-screen tiles fetch
                // as before.
                loading="lazy"
                decoding="async"
              />
            ) : (
              <MiniatureTextThumb slot={slot} envelope={envelope} />
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Unique key per slot for React reconciliation. Scene slots key on
 * pageNo (multiple scenes exist); the other five slot kinds have
 * exactly one instance each and key on the kind name directly.
 */
function thumbKey(slot: ReviewSlot): string {
  if (slot.kind === 'scene') return `scene-${slot.pageNo}`;
  return slot.kind;
}

/**
 * Miniature-text thumbnail for the five typeset structure slots.
 * Sebastian ruling 2026-09-07: "small version of the page." Not an
 * icon — a first-line-of-content teaser. Font size deliberately tiny
 * (Tailwind `text-[10px]` mirroring the PDF's 8 pt imprint block);
 * text is dream-navy on dream-white for four kinds and dream-white
 * on dream-navy for the back cover (matches the printed back panel).
 *
 * Title page uses `envelope.title` (fallback to the slot label when
 * title is empty — pre-P5-3h book — so the thumbnail always renders
 * SOMETHING recognisable). Back cover truncates the summary at ~30
 * chars for the tile. About-book uses the slot label so the tile
 * reads as a page-name teaser rather than a jumble of date + name +
 * stadt fragments.
 */
function MiniatureTextThumb({
  slot,
  envelope,
}: {
  readonly slot: ReviewSlot;
  readonly envelope: PreviewEnvelope;
}) {
  const isBackCover = slot.kind === 'back-cover';
  const text = miniatureTextForSlot(slot, envelope);
  return (
    <div
      aria-hidden="true"
      className={
        (isBackCover ? 'text-dream-white' : 'text-dream-navy') +
        ' absolute inset-0 flex flex-col items-center justify-center gap-0.5 px-1 text-center'
      }
    >
      <span className="text-[10px] leading-tight font-medium line-clamp-3">{text}</span>
    </div>
  );
}

function miniatureTextForSlot(slot: ReviewSlot, envelope: PreviewEnvelope): string {
  switch (slot.kind) {
    case 'title-page':
      return envelope.title !== '' ? envelope.title : STRINGS.book.review.titlePageLabel;
    case 'about-book':
      return STRINGS.book.review.aboutBookLabel;
    case 'about-dreamable':
      return STRINGS.book.review.aboutDreamableLabel;
    case 'imprint':
      return STRINGS.book.review.imprintLabel;
    case 'back-cover': {
      const summary = envelope.zusammenfassung;
      if (summary === '') return STRINGS.book.review.backCoverLabel;
      return summary.length > 30 ? `${summary.slice(0, 30)}…` : summary;
    }
    case 'front-cover':
    case 'scene':
      // Image slots resolve to an <img> above; this branch is
      // unreachable under normal use. Return the slot label as the
      // safe fallback for the "image URL was null but slot is not
      // typeset" edge case (rendering + placeholder).
      return slot.label;
  }
}

/** Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — hard-cap
 *  on the cover title input on the failed-tile "Anderer Titel" path.
 *  fix/text-field-limits (Sebastian ruling 2026-09-29) — cap now
 *  imported from `constants/text-limits.ts` (single PWA source) instead
 *  of a local literal. Server re-checks via `title_too_long`. */
const COVER_ANOTHER_TITLE_MAX_CHARS = COVER_REGEN_TITLE_MAX_LENGTH;

/**
 * Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — inline
 * title input for the failed-cover "Anderer Titel" path. Renders
 * below the failed cover tile when the customer clicks "Anderer
 * Titel". Save fires the standard cover-regen endpoint with the new
 * title; Abbrechen closes without writing.
 *
 * Kept inline (not modal) so the customer sees the failed cover tile
 * alongside the field he is filling. No moderation gate client-side
 * — the server refuses inappropriate content via the future
 * Jugendschutz slice (title-specific prompt) at the cover-regen
 * endpoint.
 */
/**
 * Sebastian correction slice 2026-09-10 (Part 3.3) — cover CONTENT-
 * REFUSED inline title input. Fires when
 * `envelope.cover.contentRefused === true` on a failed cover tile.
 * Shape: label ("Wie soll euer Buch heißen?") + input prefilled with
 * envelope.title + single rebuild button ("Titel ändern und neu
 * zeichnen"). No Abbrechen — the customer is in a state where the
 * cover cannot ship without a fix, so a Cancel affordance would just
 * leave the failed tile behind. Reuses the 40-char cap from
 * `CoverAnotherTitleInput` (Part 3.2 sibling); save fires the same
 * `regenCover({title, comment: null})` endpoint so the two paths
 * share transport shape.
 */
function CoverContentRefusedInput({
  initialTitle,
  onSave,
  pending,
}: {
  readonly initialTitle: string;
  readonly onSave: (newTitle: string) => void;
  readonly pending: boolean;
}) {
  const [value, setValue] = useState(initialTitle);
  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= COVER_ANOTHER_TITLE_MAX_CHARS && !pending;
  const copy = STRINGS.book.review.failureState.coverContentRefused;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dream-navy/20 bg-dream-white p-4">
      <label
        className="text-sm font-semibold text-dream-navy"
        htmlFor="cover-content-refused-title-input"
      >
        {copy.titleInputLabel}
      </label>
      <input
        id="cover-content-refused-title-input"
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={COVER_ANOTHER_TITLE_MAX_CHARS}
        aria-label={copy.titleInputLabel}
        className="h-10 rounded-md border border-dream-navy/30 bg-dream-white px-3 text-sm text-dream-navy focus-visible:outline-2 focus-visible:outline-dream-navy/50"
      />
      {/* fix/text-field-limits (Sebastian ruling 2026-09-29) — the
          shared CharacterCounter component replaces the pre-existing
          manual "{n}/{max}" line so this input matches the rest of
          the book flow (green-in-range / red-over-limit, aria-live). */}
      <CharacterCounter
        value={value.length}
        corridor={{ min: 0, max: COVER_ANOTHER_TITLE_MAX_CHARS }}
      />
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => onSave(trimmed)}
          disabled={!canSave}
          aria-busy={pending}
          className="flex h-10 items-center justify-center gap-2 rounded-lg bg-dream-gold px-4 text-sm font-bold text-dream-navy hover:opacity-90 disabled:opacity-60"
        >
          {pending && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
          {copy.rebuildButtonLabel}
        </button>
      </div>
    </div>
  );
}

// Sebastian correction slice 2026-09-10 (Part 3.2) — the pre-slice
// `CoverAnotherTitleInput` component was removed. The technical cover
// failure branch now renders "Nochmal zeichnen" only (no second
// "Anderer Titel" button, no inline title input on the technical
// path). The content-refused branch (Part 3.3) uses the
// `CoverContentRefusedInput` above with a different label + button
// text. If you are looking for the old inline title-input from
// commit 025749c, it lived in `CoverAnotherTitleInput` and was
// dropped here in the correction slice.
